/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable no-nested-ternary */
import React, { forwardRef, memo, useEffect, useRef, useState } from "react";
import {
  Checkbox,
  FormHelperText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { VariableSizeList, areEqual } from "react-window";
import PropTypes from "prop-types";

const cellHeightWithoutError = 40;
const cellHeightWithError = 120;
const tableCellHeaderHeight = "100px";

const tableCellDefaultStyle = {
  height: `${cellHeightWithoutError}px`,
  borderLeft: "0.0625rem solid #f0f2f5",
  borderRight: "0.0625rem solid #f0f2f5",
  padding: "0px 10px",
};
const tableCellErrorStyle = {
  height: `${cellHeightWithError}px`,
  border: `2px solid red`,
};
const columnSizes = {
  excludeFromImport: {
    minWidth: "80px",
    width: "80px",
  },
  name: {
    minWidth: "140px",
    width: "140px",
  },
  email: {
    minWidth: "160px",
    width: "160px",
  },
  role: {
    minWidth: "140px",
    width: "140px",
  },
  permissionsProfile: {
    minWidth: "105px",
    width: "105px",
  },
  team: {
    minWidth: "120px",
    width: "120px",
  },
  reportsTo: {
    minWidth: "140px",
    width: "140px",
  },
  annualQuota: {
    minWidth: "120px",
    width: "120px",
  },
  displayQuotaAs: {
    minWidth: "130px",
    width: "130px",
  },
  timeZone: {
    minWidth: "105px",
    width: "105px",
  },
};

const tableCellHeight = (error) => ({
  height: `${error ? cellHeightWithError : cellHeightWithoutError}px`,
});

const rows = memo(
  ({ index, style, data: { control, errors, register, initialErrorValidator } }) => (
    <TableRow key={index} hover style={{ ...style }} component="div">
      <TableCell
        component="div"
        align="center"
        sx={{
          ...columnSizes.excludeFromImport,
          ...tableCellDefaultStyle,
          ...tableCellHeight(errors?.bulkUploadUsers?.[index]),
        }}
      >
        <Controller
          render={({ field }) => (
            <Checkbox
              {...field}
              checked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
            />
          )}
          name={`bulkUploadUsers.${index}.excludeFromImport`}
          control={control}
        />
      </TableCell>
      <TableCell
        component="div"
        sx={{
          ...columnSizes.name,
          ...(errors.bulkUploadUsers?.[index]?.name ? tableCellErrorStyle : tableCellDefaultStyle),
          ...tableCellHeight(errors?.bulkUploadUsers?.[index]),
        }}
      >
        <MDInput
          variant="standard"
          InputProps={{
            disableUnderline: true,
          }}
          {...register(`bulkUploadUsers.${index}.name`, {
            required: true,
            validate: (value) => initialErrorValidator(value, index, "name"),
          })}
        />
        {errors.bulkUploadUsers?.[index]?.name?.message ? (
          <FormHelperText>{errors.bulkUploadUsers?.[index]?.name?.message}</FormHelperText>
        ) : null}
      </TableCell>
      <TableCell
        component="div"
        sx={{
          ...columnSizes.email,
          ...(errors.bulkUploadUsers?.[index]?.email ? tableCellErrorStyle : tableCellDefaultStyle),
          ...tableCellHeight(errors?.bulkUploadUsers?.[index]),
        }}
      >
        <MDInput
          variant="standard"
          InputProps={{
            disableUnderline: true,
          }}
          {...register(`bulkUploadUsers.${index}.email`, {
            required: true,
            validate: (value) => initialErrorValidator(value, index, "email"),
          })}
        />
        {errors.bulkUploadUsers?.[index]?.email?.message ? (
          <FormHelperText>{errors.bulkUploadUsers?.[index]?.email?.message}</FormHelperText>
        ) : null}
      </TableCell>
      <TableCell
        component="div"
        sx={{
          ...columnSizes.role,
          ...(errors.bulkUploadUsers?.[index]?.role ? tableCellErrorStyle : tableCellDefaultStyle),
          ...tableCellHeight(errors?.bulkUploadUsers?.[index]),
        }}
      >
        <MDInput
          variant="standard"
          InputProps={{
            disableUnderline: true,
          }}
          {...register(`bulkUploadUsers.${index}.role`, {
            required: true,
            validate: (value) => initialErrorValidator(value, index, "role"),
          })}
        />
        {errors.bulkUploadUsers?.[index]?.role?.message ? (
          <FormHelperText>{errors.bulkUploadUsers?.[index]?.role?.message}</FormHelperText>
        ) : null}
      </TableCell>
      <TableCell
        component="div"
        sx={{
          ...columnSizes.permissionsProfile,
          ...(errors.bulkUploadUsers?.[index]?.permissionsProfile
            ? tableCellErrorStyle
            : tableCellDefaultStyle),
          ...tableCellHeight(errors?.bulkUploadUsers?.[index]),
        }}
      >
        <MDInput
          variant="standard"
          InputProps={{
            disableUnderline: true,
          }}
          {...register(`bulkUploadUsers.${index}.permissionsProfile`, {
            required: true,
            validate: (value) => initialErrorValidator(value, index, "permissionsProfile"),
          })}
        />
        {errors.bulkUploadUsers?.[index]?.permissionsProfile?.message ? (
          <FormHelperText>
            {errors.bulkUploadUsers?.[index]?.permissionsProfile?.message}
          </FormHelperText>
        ) : null}
      </TableCell>
      <TableCell
        component="div"
        sx={{
          ...columnSizes.team,
          ...(errors.bulkUploadUsers?.[index]?.team ? tableCellErrorStyle : tableCellDefaultStyle),
          ...tableCellHeight(errors?.bulkUploadUsers?.[index]),
        }}
      >
        <MDInput
          variant="standard"
          InputProps={{
            disableUnderline: true,
          }}
          {...register(`bulkUploadUsers.${index}.team`, {
            validate: (value) => initialErrorValidator(value, index, "team"),
          })}
        />
        {errors.bulkUploadUsers?.[index]?.team?.message ? (
          <FormHelperText>{errors.bulkUploadUsers?.[index]?.team?.message}</FormHelperText>
        ) : null}
      </TableCell>
      <TableCell
        component="div"
        sx={{
          ...columnSizes.reportsTo,
          ...(errors.bulkUploadUsers?.[index]?.reportsTo
            ? tableCellErrorStyle
            : tableCellDefaultStyle),
          ...tableCellHeight(errors?.bulkUploadUsers?.[index]),
        }}
      >
        <MDInput
          variant="standard"
          InputProps={{
            disableUnderline: true,
          }}
          {...register(`bulkUploadUsers.${index}.reportsTo`, {
            validate: (value) => initialErrorValidator(value, index, "reportsTo"),
          })}
        />
        {errors.bulkUploadUsers?.[index]?.reportsTo?.message ? (
          <FormHelperText>{errors.bulkUploadUsers?.[index]?.reportsTo?.message}</FormHelperText>
        ) : null}
      </TableCell>
      <TableCell
        component="div"
        sx={{
          ...columnSizes.annualQuota,
          ...(errors.bulkUploadUsers?.[index]?.annualQuota
            ? tableCellErrorStyle
            : tableCellDefaultStyle),
          ...tableCellHeight(errors?.bulkUploadUsers?.[index]),
        }}
      >
        <MDInput
          type="number"
          variant="standard"
          InputProps={{
            disableUnderline: true,
          }}
          {...register(`bulkUploadUsers.${index}.annualQuota`, {
            validate: (value) => initialErrorValidator(value, index, "annualQuota"),
          })}
        />
        {errors.bulkUploadUsers?.[index]?.annualQuota?.message ? (
          <FormHelperText>{errors.bulkUploadUsers?.[index]?.annualQuota?.message}</FormHelperText>
        ) : null}
      </TableCell>
      <TableCell
        component="div"
        sx={{
          ...columnSizes.displayQuotaAs,
          ...(errors.bulkUploadUsers?.[index]?.displayQuotaAs
            ? tableCellErrorStyle
            : tableCellDefaultStyle),
          ...tableCellHeight(errors?.bulkUploadUsers?.[index]),
        }}
      >
        <MDInput
          variant="standard"
          InputProps={{
            disableUnderline: true,
          }}
          {...register(`bulkUploadUsers.${index}.displayQuotaAs`, {
            validate: (value) => initialErrorValidator(value, index, "displayQuotaAs"),
          })}
        />
        {errors.bulkUploadUsers?.[index]?.displayQuotaAs?.message ? (
          <FormHelperText sx={{ fontSize: "11px" }}>
            {errors.bulkUploadUsers?.[index]?.displayQuotaAs?.message}
          </FormHelperText>
        ) : null}
      </TableCell>
      <TableCell
        component="div"
        sx={{
          ...columnSizes.timeZone,
          ...(errors.bulkUploadUsers?.[index]?.timeZone
            ? tableCellErrorStyle
            : tableCellDefaultStyle),
          ...tableCellHeight(errors?.bulkUploadUsers?.[index]),
        }}
      >
        <MDInput
          variant="standard"
          InputProps={{
            disableUnderline: true,
          }}
          {...register(`bulkUploadUsers.${index}.timeZone`, {
            validate: (value) => initialErrorValidator(value, index, "timeZone"),
          })}
        />
        {errors.bulkUploadUsers?.[index]?.timeZone?.message ? (
          <FormHelperText>{errors.bulkUploadUsers?.[index]?.timeZone?.message}</FormHelperText>
        ) : null}
      </TableCell>
    </TableRow>
  ),
  areEqual
);

const Validate = forwardRef(
  (
    {
      setShouldRevalidate,
      isValidBulkUploadData,
      bulkUploadUsersData,
      reValidateBulkUpload,
      handleBulkUserSubmit,
      subscriptionData,
    },
    ref
  ) => {
    const listRef = useRef({});
    const [initialValues, setInitialValues] = useState({
      bulkUploadUsers: bulkUploadUsersData?.map((user) => ({
        excludeFromImport: user?.excludeFromImport?.value || false,
        name: user?.Name?.value,
        email: user?.Email?.value,
        role: user?.Role?.value,
        permissionsProfile: user?.["Permissions Profile"]?.value,
        team: user?.Team?.value,
        reportsTo: user?.["Reports to"]?.value,
        annualQuota: user?.["Annual Quota"]?.value,
        displayQuotaAs: user?.["Display Quota as"]?.value,
        timeZone: user?.TimeZone?.value,
      })),
    });

    const {
      register,
      control,
      handleSubmit,
      formState: { errors, isDirty },
      setError,
      clearErrors,
      getValues,
    } = useForm({
      reValidateMode: "all",
      mode: "all",
      values: initialValues,
    });

    const { fields } = useFieldArray({
      control,
      name: "bulkUploadUsers",
    });

    const convertedKeys = {
      Name: "name",
      Email: "email",
      Role: "role",
      "Permissions Profile": "permissionsProfile",
      Team: "team",
      "Reports to": "reportsTo",
      "Annual Quota": "annualQuota",
      "Display Quota as": "displayQuotaAs",
      TimeZone: "timeZone",
    };

    const initialErrorValidator = (value, index, key) => {
      if (
        value &&
        key === "displayQuotaAs" &&
        !["monthly", "quarterly", "annual"].includes(value?.toLowerCase())
      ) {
        return "Invalid value (monthly, quarterly, annual)";
      }
      const convertedKey =
        Object.keys(convertedKeys).find((objectKey) => convertedKeys[objectKey] === key) || key;
      if (value === bulkUploadUsersData?.[index]?.[convertedKey]?.value)
        return bulkUploadUsersData?.[index]?.[convertedKey]?.error;
      return true;
    };

    const onSubmit = async (data, type) => {
      const bulkImportData =
        data?.bulkUploadUsers?.map((obj) => {
          const newObj = {};
          Object.keys(obj).forEach((key) => {
            const convertedKey =
              Object.keys(convertedKeys).find((objectKey) => convertedKeys[objectKey] === key) ||
              key;
            newObj[convertedKey] = obj[key];
          });
          return newObj;
        }) || [];

      if (type === "revalidate") return reValidateBulkUpload(bulkImportData || []);

      return handleBulkUserSubmit(bulkImportData || []);
    };

    useEffect(() => {
      setShouldRevalidate(isDirty);
    }, [isDirty]);

    useEffect(() => {
      setInitialValues({
        bulkUploadUsers: bulkUploadUsersData?.map((user) => ({
          excludeFromImport: user?.excludeFromImport?.value || false,
          name: user?.Name?.value,
          email: user?.Email?.value,
          role: user?.Role?.value,
          permissionsProfile: user?.["Permissions Profile"]?.value,
          team: user?.Team?.value,
          reportsTo: user?.["Reports to"]?.value,
          annualQuota: user?.["Annual Quota"]?.value,
          displayQuotaAs: user?.["Display Quota as"]?.value,
          timeZone: user?.TimeZone?.value,
        })),
      });
    }, [bulkUploadUsersData]);

    useEffect(() => {
      // eslint-disable-next-line no-plusplus
      for (let index = 0; index < bulkUploadUsersData.length; index++) {
        const element = bulkUploadUsersData[index];
        // eslint-disable-next-line no-restricted-syntax
        for (const [key, valueObject] of Object.entries(element)) {
          if (valueObject.error)
            setError(`bulkUploadUsers.${index}.${convertedKeys[key]}`, {
              type: "custom",
              message: valueObject.error,
            });
        }
      }
    }, [setError, initialValues]);

    const triggerSubmit = async (type) => {
      clearErrors("bulkUploadUsers");
      const values = getValues();
      await onSubmit(values, type);
    };

    // Expose the triggerSubmit function through the ref
    React.useImperativeHandle(ref, () => ({
      triggerSubmit,
    }));

    const getSize = (index) =>
      errors?.bulkUploadUsers?.[index] ? cellHeightWithError : cellHeightWithoutError;

    useEffect(() => {
      listRef.current.resetAfterIndex(0);
    }, [errors?.bulkUploadUsers?.filter((error) => error)?.length]);

    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        {bulkUploadUsersData && bulkUploadUsersData.length ? (
          isValidBulkUploadData ? (
            <MDTypography variant="h5" mx={2} my={2} color="success" textAlign="center">
              Everything looks great! Validate the users below, you can also make any changes to the
              below users on this page.
            </MDTypography>
          ) : (
            <MDTypography variant="h5" mx={2} my={2} color="error" textAlign="center">
              Errors were found with the file, please correct the users or exclude them before
              proceeding.
            </MDTypography>
          )
        ) : null}
        {bulkUploadUsersData &&
        bulkUploadUsersData.length &&
        subscriptionData?.subscriptionError ? (
          <MDTypography variant="h5" mx={2} my={2} color="error" textAlign="center">
            {subscriptionData.subscriptionError}
          </MDTypography>
        ) : null}
        <TableContainer component={Paper}>
          <Table stickyHeader aria-label="sticky table" component="div">
            <TableHead sx={{ padding: 0 }} component="div">
              <TableRow component="div">
                <TableCell
                  component="div"
                  sx={{
                    ...columnSizes.excludeFromImport,
                    ...tableCellDefaultStyle,
                    height: tableCellHeaderHeight,
                  }}
                >
                  Exclude from import
                </TableCell>
                <TableCell
                  component="div"
                  sx={{
                    ...columnSizes.name,
                    ...tableCellDefaultStyle,
                    height: tableCellHeaderHeight,
                  }}
                >
                  Name
                </TableCell>
                <TableCell
                  component="div"
                  sx={{
                    ...columnSizes.email,
                    ...tableCellDefaultStyle,
                    height: tableCellHeaderHeight,
                  }}
                >
                  Email
                </TableCell>
                <TableCell
                  component="div"
                  sx={{
                    ...columnSizes.role,
                    ...tableCellDefaultStyle,
                    height: tableCellHeaderHeight,
                  }}
                >
                  Role
                </TableCell>
                <TableCell
                  component="div"
                  sx={{
                    ...columnSizes.permissionsProfile,
                    ...tableCellDefaultStyle,
                    height: tableCellHeaderHeight,
                  }}
                >
                  Permissions Profile
                </TableCell>
                <TableCell
                  component="div"
                  sx={{
                    ...columnSizes.team,
                    ...tableCellDefaultStyle,
                    height: tableCellHeaderHeight,
                  }}
                >
                  Team
                </TableCell>
                <TableCell
                  component="div"
                  sx={{
                    ...columnSizes.reportsTo,
                    ...tableCellDefaultStyle,
                    height: tableCellHeaderHeight,
                  }}
                >
                  Reports to
                </TableCell>
                <TableCell
                  component="div"
                  sx={{
                    ...columnSizes.annualQuota,
                    ...tableCellDefaultStyle,
                    height: tableCellHeaderHeight,
                  }}
                >
                  Annual Quota
                </TableCell>
                <TableCell
                  component="div"
                  sx={{
                    ...columnSizes.displayQuotaAs,
                    ...tableCellDefaultStyle,
                    height: tableCellHeaderHeight,
                  }}
                >
                  Display Quota as
                </TableCell>
                <TableCell
                  component="div"
                  sx={{
                    ...columnSizes.timeZone,
                    ...tableCellDefaultStyle,
                    height: tableCellHeaderHeight,
                  }}
                >
                  Timezone
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody component="div">
              <VariableSizeList
                height={500}
                width="100%"
                itemSize={getSize}
                itemCount={fields.length}
                itemData={{ control, errors, register, initialErrorValidator }}
                ref={listRef}
              >
                {rows}
              </VariableSizeList>
            </TableBody>
          </Table>
        </TableContainer>
      </form>
    );
  }
);

export default Validate;

Validate.defaultProps = {
  setShouldRevalidate: () => {},
  isValidBulkUploadData: false,
  bulkUploadUsersData: [],
  reValidateBulkUpload: () => {},
  handleBulkUserSubmit: () => {},
};

Validate.propTypes = {
  setShouldRevalidate: PropTypes.func,
  isValidBulkUploadData: PropTypes.bool,
  bulkUploadUsersData: PropTypes.arrayOf(),
  reValidateBulkUpload: PropTypes.func,
  handleBulkUserSubmit: PropTypes.func,
};
